import { FC, useEffect, useMemo, useState } from 'react'
import {
  BusinessUnitOption,
  FormSearch,
  FormText,
  FormTextArea,
  InventoryImportProductsList,
  ModalQuestion,
  PrimaryButton,
} from '..'
import { PencilIcon, TrashIcon } from '@heroicons/react/24/outline'
import { GeneralTypePhoneServices } from '../../services/GeneralServices'
import { GeneralTypeInterface } from '../../interfaces/Dto/GeneralTypeInterface'
import { filterGeneral } from '../../utils/filters'
import type {
  InventoryImportOrder,
  InventoryImportProduct,
  OrderErrors,
} from '../../modules/Inventory/InventoryImportReview'

type OptionLike = {
  id?: any
  name?: string
  value?: any
  locationCode?: string
  locationName?: string
  abbreviationName?: string
  buCode?: string
  buName?: string
  description?: string
  location?: {
    name?: string
    locationCode?: string
    address?: string
    postalCode?: string
  }
}

export const InventoryImportAccordionRow: FC<{
  order: InventoryImportOrder
  index: number
  errors: OrderErrors
  identificationTypes: OptionLike[]
  typeShipment: OptionLike[]
  businessUnits: OptionLike[]
  cities: OptionLike[]
  pieceCategories: OptionLike[]
  onChangeOrder: (patch: Partial<InventoryImportOrder>) => void
  onChangeProduct: (
    productIndex: number,
    patch: Partial<InventoryImportProduct>
  ) => void
  onAddProduct: () => void
  onRemoveProduct: (productIndex: number) => void
  onRemoveOrder: () => void
  productsMap?: Record<string, { Name: string; Count: number }>
  productOptions?: any[]
  priorUsage?: Record<string, number>
}> = ({
  order,
  index,
  errors,
  identificationTypes,
  typeShipment,
  businessUnits,
  cities,
  pieceCategories,
  onChangeOrder,
  onChangeProduct,
  onAddProduct,
  onRemoveProduct,
  onRemoveOrder,
  productsMap,
  productOptions,
  priorUsage,
}) => {
  const hasError = useMemo(() => {
    const e = errors as any
    const orderHas = Object.keys(e || {}).some((k) => k !== 'products')
    const prodHas = (e?.products || []).some(
      (pe: any) => Object.keys(pe || {}).length > 0
    )
    return orderHas || prodHas
  }, [errors])
  const [open, setOpen] = useState(hasError)
  const [deleteModalOpen, setDeleteModalOpen] = useState(false)
  const [deleteAccepted, setDeleteAccepted] = useState(false)

  const headerBg = hasError
    ? 'bg-red-100 border-red-300'
    : 'bg-gray-50 border-gray-200'

  const idTypeValue = order.idTypeAbbr || ''
  const deliveryValue = order.deliveryTypeName || ''
  // Store display: show buName while internally storing buCode
  const buOption = useMemo(
    () => businessUnits.find((b: any) => b.buCode === order.buCode),
    [businessUnits, order.buCode]
  )
  // Mostrar código y nombre de la ubicación de la tienda: "CODIGO - Nombre"
  const buDisplayValue = buOption
    ? `${buOption.buCode || ''} - ${
        buOption.location?.name || buOption.buName || ''
      }`
        .replace(/^ - /, '')
        .replace(/ - $/, '')
        .trim()
    : ''
  // City display: show locationName but keep storing the locationCode internally
  const cityOption = useMemo(
    () => cities.find((c: any) => c.locationCode === order.cityCode),
    [cities, order.cityCode]
  )
  const cityDisplayValue = cityOption?.locationName || ''
  const isOffice = deliveryValue === 'Oficina'
  const isHome = deliveryValue === 'Domicilio'

  // --- Phone prefix separation logic ---
  const originalPhone = order.phone || ''
  const initialPrefix = originalPhone.slice(0, 3)
  const initialNumber = originalPhone.slice(3)
  const [phonePrefixList, setPhonePrefixList] = useState<string[]>([])
  const [phonePrefixSearch, setPhonePrefixSearch] = useState(initialPrefix)
  const [phoneNumberValue, setPhoneNumberValue] = useState(initialNumber)
  // Add states for search values to enable typing in FormSearch
  const [buSearchValue, setBuSearchValue] = useState(buDisplayValue)
  const [citySearchValue, setCitySearchValue] = useState(cityDisplayValue)
  const [idTypeSearchValue, setIdTypeSearchValue] = useState(idTypeValue)
  const [deliverySearchValue, setDeliverySearchValue] = useState(deliveryValue)
  useEffect(() => {
    // Initialize only on mount
    const phone = order.phone || ''
    setPhonePrefixSearch(phone.slice(0, 3))
    setPhoneNumberValue(phone.slice(3))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
  // Fetch phone prefixes
  useEffect(() => {
    const getPhonePrefixList = async () => {
      try {
        const phonetype: any = await GeneralTypePhoneServices('236')
        if (!!phonetype?.didError || !phonetype?.model) return
        const emptyPhonePrefix = { value: 'FIJO', text: 'FIJO' }
        let phonetypeList = phonetype.model as unknown as GeneralTypeInterface[]
        phonetypeList.push(emptyPhonePrefix as any)
        setPhonePrefixList(
          (phonetypeList as unknown as GeneralTypeInterface[]).map(
            (phone: any) => phone.value
          )
        )
      } catch (err) {
        // silent fail
      }
    }
    getPhonePrefixList()
  }, [])
  const handleSelectPrefix = (opt: any) => {
    const prefix = opt
    setPhonePrefixSearch(prefix || '')
    const combined = (prefix || '') + (phoneNumberValue || '')
    onChangeOrder({ phone: combined })
  }
  const handleChangeNumber = (e: any) => {
    const val = e.target.value
    // Validation: only digits
    const re = /^[0-9\b]{0,10}$/
    const hasPrefix =
      !!phonePrefixSearch &&
      (phonePrefixSearch === 'FIJO' || phonePrefixSearch.length === 3)
    if (!hasPrefix) {
      // Ignore typing when no prefix selected
      return
    }
    if (val === '' || re.test(val)) {
      setPhoneNumberValue(val)
      const combined = (phonePrefixSearch || '') + val
      onChangeOrder({ phone: combined })
    }
  }

  useEffect(() => {
    if (deleteAccepted) {
      onRemoveOrder()
      setDeleteAccepted(false)
    }
  }, [deleteAccepted, onRemoveOrder])

  // Sync search values with display values
  useEffect(() => {
    setBuSearchValue(buDisplayValue)
  }, [buDisplayValue])

  useEffect(() => {
    setCitySearchValue(cityDisplayValue)
  }, [cityDisplayValue])

  useEffect(() => {
    setIdTypeSearchValue(idTypeValue)
  }, [idTypeValue])

  useEffect(() => {
    setDeliverySearchValue(deliveryValue)
  }, [deliveryValue])

  return (
    <div className={`border rounded-md mb-2 ${open ? 'relative z-50' : ''}`}>
      <div className={`group flex items-center px-3 py-2 border-b ${headerBg}`}>
        <button
          type="button"
          className="flex-1 flex items-center justify-between text-left"
          onClick={() => setOpen((v: boolean) => !v)}
        >
          <div className="flex items-center gap-4 text-sm">
            <span className="font-semibold">#{index + 1}</span>
            <span>Pedido: {order.orderNumber || '—'}</span>
            <span>Destinatario: {order.name || '—'}</span>
          </div>
          <div
            className={`text-xs transition-transform duration-200 ${
              hasError ? 'text-red-600' : 'text-gray-500'
            } ${open ? 'transform -translate-x-1' : ''} ${
              !open ? 'group-hover:transform group-hover:-translate-x-1' : ''
            }`}
          >
            {hasError ? (
              'Con errores — click para corregir'
            ) : (
              <PencilIcon className="w-4 h-4" />
            )}
          </div>
        </button>
        <button
          type="button"
          className={`ml-2 p-1 text-red-500 hover:text-red-700 transition-opacity duration-200 ${
            open ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'
          }`}
          onClick={(e) => {
            e.stopPropagation()
            setDeleteModalOpen(true)
          }}
          title="Eliminar registro"
        >
          <TrashIcon className="w-5 h-5" />
        </button>
      </div>

      {open && (
        <div className="p-3 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 text-sm">
          <FormText
            id={`orderNumber-${index}`}
            name={`orderNumber-${index}`}
            label="Nº pedido"
            value={order.orderNumber || ''}
            error={(errors as any).orderNumber}
            onChange={(e: any) =>
              onChangeOrder({ orderNumber: e.target.value })
            }
            className="!h-8"
          />

          <FormSearch
            id={`idTypeAbbr-${index}`}
            name={`idTypeAbbr-${index}`}
            label="Tipo doc"
            placeholder="Selecciona"
            value={idTypeSearchValue}
            options={identificationTypes}
            error={(errors as any).idTypeAbbr}
            className="!h-8"
            onChange={(e: any) => setIdTypeSearchValue(e.target.value)}
            onSelectOption={(opt: any) => {
              onChangeOrder({ idTypeAbbr: opt?.abbreviationName || '' })
              setIdTypeSearchValue(opt?.abbreviationName || '')
            }}
            onFilter={(opt: any, filter: string) => {
              const tokens = filter?.toLowerCase().split(' ')
              return tokens?.every((token) => {
                return (
                  token === '-' ||
                  opt.abbreviationName?.toLowerCase().includes(token) ||
                  opt.name?.toLowerCase().includes(token)
                )
              })
            }}
            isTabled={true}
            RenderOption={({ option }: any) => (
              <div className="px-2 py-1 text-sm">
                {option.abbreviationName || option.name}
              </div>
            )}
          />

          <FormText
            id={`idNumber-${index}`}
            name={`idNumber-${index}`}
            label="Nº doc"
            value={order.idNumber || ''}
            error={(errors as any).idNumber}
            onChange={(e: any) => onChangeOrder({ idNumber: e.target.value })}
            className="!h-8"
          />

          <FormText
            id={`name-${index}`}
            name={`name-${index}`}
            label="Nombre"
            value={order.name || ''}
            error={(errors as any).name}
            onChange={(e: any) => onChangeOrder({ name: e.target.value })}
            className="!h-8"
          />

          <div className="flex gap-3 items-end">
            <FormSearch
              id={`phonePrefix-${index}`}
              name={`phonePrefix-${index}`}
              label="Operadora"
              value={phonePrefixSearch}
              options={phonePrefixList}
              unselect={!!phonePrefixSearch}
              className="!h-8 w-32"
              labelClassname="!text-xs"
              containerClassname="flex flex-col"
              onChange={(e: any) => {
                const val = e.target.value
                if (!val) {
                  // X pressed (cleared). Reset prefix & number and clear phone field.
                  setPhonePrefixSearch('')
                  setPhoneNumberValue('')
                  onChangeOrder({ phone: '' })
                } else {
                  setPhonePrefixSearch(val)
                }
              }}
              onSelectOption={(option: any) => handleSelectPrefix(option)}
              onFilter={filterGeneral as any}
              RenderOption={({ option }: any) => (
                <div className="px-2 py-1 text-sm">{option}</div>
              )}
            />
            <FormText
              id={`phoneNumber-${index}`}
              name={`phoneNumber-${index}`}
              label="Teléfono"
              value={phoneNumberValue}
              onChange={handleChangeNumber}
              maxLength={phonePrefixSearch !== 'FIJO' ? 7 : 10}
              className="!h-8 w-40"
              labelClassname="!text-xs"
              containerClassname="flex flex-col"
              disabled={
                !phonePrefixSearch ||
                (phonePrefixSearch !== 'FIJO' && phonePrefixSearch.length !== 3)
              }
            />
          </div>

          <FormText
            id={`email-${index}`}
            name={`email-${index}`}
            label="Email"
            value={order.email || ''}
            error={(errors as any).email}
            onChange={(e: any) => onChangeOrder({ email: e.target.value })}
            type="email"
            required={true}
            className="!h-8"
          />

          <FormSearch
            id={`delivery-${index}`}
            name={`delivery-${index}`}
            label="Tipo de entrega"
            placeholder="Oficina / Domicilio"
            value={deliverySearchValue}
            options={typeShipment}
            error={(errors as any).deliveryTypeName}
            className="!h-8"
            onChange={(e: any) => setDeliverySearchValue(e.target.value)}
            onSelectOption={(opt: any) => {
              const val = opt?.name || ''
              const patch: any = { deliveryTypeName: val }
              if (val === 'Domicilio') {
                patch.buCode = '' // clear tienda
                patch.address = ''
                patch.postalCode = ''
              } else if (val === 'Oficina') {
                patch.cityCode = '' // will be derived from tienda
                patch.address = ''
                patch.postalCode = ''
              }
              onChangeOrder(patch)
              setDeliverySearchValue(val)
            }}
            onFilter={(opt: any, filter: string) => {
              const tokens = filter?.toLowerCase().split(' ')
              return tokens?.every((token) => {
                return token === '-' || opt.name?.toLowerCase().includes(token)
              })
            }}
            isTabled={true}
            RenderOption={({ option }: any) => (
              <div className="px-2 py-1 text-sm">{option.name}</div>
            )}
          />
          {isOffice && (
            <FormSearch
              id={`bu-${index}`}
              name={`bu-${index}`}
              label="Tienda"
              placeholder="Ingresa la tienda"
              value={buSearchValue}
              options={businessUnits}
              error={(errors as any).buCode}
              className="!h-8"
              onChange={(e: any) => setBuSearchValue(e.target.value)}
              onSelectOption={(opt: any) => {
                const patch: any = { buCode: opt?.buCode }
                const locCode = opt?.location?.locationCode || opt?.locationCode
                if (locCode) patch.cityCode = locCode
                // Set address from store and lock via disabled prop on address field
                const addr = opt?.location?.address || opt?.address || ''
                patch.address = addr?.trim() || undefined
                const postal = opt?.location?.postalCode || opt?.postalCode
                if (postal) patch.postalCode = postal
                onChangeOrder(patch)
                // Update search value to display value
                const display = opt
                  ? `${opt.buCode || ''} - ${
                      opt.location?.name || opt.buName || ''
                    }`
                      .replace(/^ - /, '')
                      .replace(/ - $/, '')
                      .trim()
                  : ''
                setBuSearchValue(display)
              }}
              onFilter={(opt: any, filter: string) => {
                const tokens = filter?.toLowerCase()?.split(' ') ?? []
                return tokens.every((token) => {
                  return (
                    token === '-' ||
                    opt.buName?.toLowerCase().includes(token) ||
                    opt.buCode?.toLowerCase().includes(token) ||
                    opt.location?.name?.toLowerCase().includes(token) ||
                    opt.location?.locationCode?.toLowerCase().includes(token)
                  )
                })
              }}
              isTabled={true}
              RenderOption={({ option }: any) => (
                <BusinessUnitOption {...option} />
              )}
            />
          )}

          {(isHome || isOffice) && (
            <FormSearch
              id={`city-${index}`}
              name={`city-${index}`}
              label="Ciudad"
              placeholder={
                isOffice ? 'Ciudad de la tienda' : 'Ingresa la ciudad'
              }
              value={citySearchValue}
              options={cities}
              error={(errors as any).cityCode}
              className="!h-8"
              disabled={isOffice}
              onChange={(e: any) => {
                if (!isOffice) {
                  setCitySearchValue(e.target.value)
                  onChangeOrder({ cityCode: e.target.value })
                }
              }}
              onSelectOption={(opt: any) => {
                if (!isOffice) {
                  onChangeOrder({ cityCode: opt?.locationCode })
                  setCitySearchValue(opt?.locationName || '')
                }
              }}
              onFilter={(opt: any, filter: string) => {
                const tokens = filter?.toLowerCase().split(' ')
                return tokens?.every((token) => {
                  return (
                    token === '-' ||
                    opt.locationCode?.toLowerCase().includes(token) ||
                    opt.locationName?.toLowerCase().includes(token)
                  )
                })
              }}
              isTabled={true}
              RenderOption={({ option }: any) => (
                <div className="px-2 py-1 text-sm">{option.locationName}</div>
              )}
            />
          )}

          <FormTextArea
            id={`address-${index}`}
            name={`address-${index}`}
            label="Dirección"
            value={order.address || ''}
            error={
              !order.address ? 'Dirección requerida.' : (errors as any).address
            }
            onChange={(e: any) => onChangeOrder({ address: e.target.value })}
            disabled={isOffice}
            required={true}
            className="min-h-[6rem]"
            labelClassname="text-md"
          />

          <FormText
            id={`postal-${index}`}
            name={`postal-${index}`}
            label="Código postal"
            value={order.postalCode || ''}
            onChange={(e: any) => onChangeOrder({ postalCode: e.target.value })}
            className="!h-8"
          />

          <FormTextArea
            id={`notes-${index}`}
            name={`notes-${index}`}
            label="Observaciones"
            value={order.notes || ''}
            onChange={(e: any) => onChangeOrder({ notes: e.target.value })}
            className="min-h-[6rem]"
            labelClassname="text-md"
          />

          <FormText
            id={`declared-total-${index}`}
            name={`declared-total-${index}`}
            label="Valor declarado total"
            value={(
              (order.products || []).reduce(
                (acc, p) => acc + (p.declaredValue || 0) * (p.quantity ?? 1),
                0
              ) || 0
            ).toFixed(2)}
            disabled={true}
            className="!h-8"
            labelClassname="text-md"
          />

          <div className="col-span-full">
            <InventoryImportProductsList
              orderIndex={index}
              products={order.products || []}
              errors={(errors as any).products || []}
              productOptions={productOptions || []}
              productsMap={productsMap || {}}
              pieceCategories={pieceCategories}
              onChangeProduct={(
                pIdx: number,
                patch: Partial<InventoryImportProduct>
              ) => onChangeProduct(pIdx, patch)}
              onAddProduct={() => onAddProduct()}
              onRemoveProduct={(pIdx) => onRemoveProduct(pIdx)}
              priorUsage={priorUsage}
            />
          </div>

          <div className="col-span-full flex justify-end mt-2">
            <div className="flex items-center gap-3">
              {hasError && (
                <span className="text-xs text-red-600">
                  Corrige los campos marcados antes de cerrar
                </span>
              )}
              <PrimaryButton
                className="sm:w-32"
                onClick={() => {
                  if (!hasError) setOpen(false)
                }}
                disabled={hasError}
              >
                {hasError ? 'Pendiente' : 'Listo'}
              </PrimaryButton>
            </div>
          </div>
        </div>
      )}
      <ModalQuestion
        open={deleteModalOpen}
        setOpen={setDeleteModalOpen}
        setAccepts={setDeleteAccepted}
        title="¿Estás seguro de que quieres eliminar este registro?"
      />
    </div>
  )
}
